import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';

export type PwaPromptPreference = 'remind_later' | 'never';

interface UsePwaPromptPreferenceResult {
  preference: PwaPromptPreference | null;
  isLoading: boolean;
  updatePreference: (newPreference: PwaPromptPreference) => Promise<void>;
}

export const usePwaPromptPreference = (): UsePwaPromptPreferenceResult => {
  const { user } = useAuth();
  const [preference, setPreference] = useState<PwaPromptPreference | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load preference from Supabase
  useEffect(() => {
    const loadPreference = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('pwa_install_prompt_preference')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('[PWA Preference] Error loading preference:', error);
          setPreference('remind_later'); // Default
        } else {
          setPreference((data?.pwa_install_prompt_preference as PwaPromptPreference) || 'remind_later');
        }
      } catch (error) {
        console.error('[PWA Preference] Error:', error);
        setPreference('remind_later');
      } finally {
        setIsLoading(false);
      }
    };

    loadPreference();
  }, [user?.id]);

  // Update preference in Supabase
  const updatePreference = useCallback(async (newPreference: PwaPromptPreference) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ pwa_install_prompt_preference: newPreference })
        .eq('id', user.id);

      if (error) {
        console.error('[PWA Preference] Error updating preference:', error);
        throw error;
      }

      setPreference(newPreference);
      console.log('[PWA Preference] Updated to:', newPreference);
    } catch (error) {
      console.error('[PWA Preference] Update failed:', error);
      throw error;
    }
  }, [user?.id]);

  return {
    preference,
    isLoading,
    updatePreference
  };
};
